// src/utils/proposalProse.js
//
// The prose renderer for §3.3, §3.4, §3.7, §3.8 and §3.10 — the drawRuns pass
// that proposalLockedCopy's run arrays were waiting for.
//
// WHY THIS IS ITS OWN MODULE: word wrap has to work ACROSS run boundaries. A
// bold lead followed by regular text is one continuous paragraph, so wrapping
// each run independently would break the line at every emphasis change — the
// bullets in §3.3 would each break right after their bold lead. Instead the
// whole run array is tokenized into words, every word carries the face(s) it is
// set in, and lines are filled greedily with each token measured in its own
// font. A single word may even span runs (`**bold**tail`), so a word is a list
// of segments rather than a string.
//
// All measurement and drawing goes through pdfText. widthOfTextAtSize is never
// called here: pdf-lib measures kerned and renders unkerned.

import { measureTracked, drawTracked } from './pdfText';
import {
  TYPE,
  BODY_LEADING,
  MARGIN_LEFT,
  CONTENT_WIDTH,
} from './proposalLayout';

/** §3.4: "paragraphs separated by 8 pt". */
export const PARAGRAPH_GAP = 8;

/** Air beneath a heading before its body copy. Not a spec value. */
const HEADING_GAP = 4;

/**
 * Resolve a run's face to one of the two embedded fonts.
 *
 * ITALIC HAS NO FACE. §1.3 permits Helvetica and Helvetica-Bold only — "no
 * embedding, no second typeface" — and createProposalContext embeds exactly
 * those two. Helvetica-Oblique is not available, so italic runs render as
 * regular. The emphasis is preserved in the data (proposalLockedCopy marks
 * `*you*` italic) and is simply not expressible in this face set; adding an
 * oblique would be a §6 change-control decision, not a renderer fix.
 */
function faceFor(run) {
  return run && run.bold === true ? 'bold' : 'regular';
}

/** A plain string is equivalent to a single non-bold run. */
export function normalizeRuns(textOrRuns) {
  if (typeof textOrRuns === 'string') return [{ text: textOrRuns }];
  if (!Array.isArray(textOrRuns)) return [];
  return textOrRuns.filter((r) => r && typeof r.text === 'string');
}

/**
 * Split a run array into words, where a word is a list of { text, face }
 * segments. Consecutive non-whitespace across a run boundary joins into one
 * word; whitespace separates words and is not retained.
 */
function toWords(runs) {
  const words = [];
  let current = null;
  for (const run of runs) {
    const face = faceFor(run);
    for (const part of String(run.text).split(/(\s+)/)) {
      if (part === '') continue;
      if (/^\s+$/.test(part)) {
        current = null;
        continue;
      }
      if (current === null) {
        current = { segments: [] };
        words.push(current);
      }
      current.segments.push({ text: part, face });
    }
  }
  return words;
}

function segmentsWidth(ctx, segments, size, tracking) {
  return segments.reduce(
    (a, s) => a + measureTracked(ctx.fonts[s.face], s.text, size, tracking),
    0,
  );
}

/**
 * Wrap a run array into lines, measuring each token in its own font.
 *
 * With `indent`, the FIRST line gets the full maxWidth and every continuation
 * line gets maxWidth - indent, so a hanging-indented block keeps a straight
 * right edge instead of overrunning it.
 *
 * @param {Object} ctx proposal context
 * @param {string|Array} textOrRuns
 * @param {Object} opts
 * @param {number} opts.size
 * @param {number} opts.maxWidth
 * @param {number} [opts.indent=0] hanging indent applied to continuation lines
 * @param {number} [opts.tracking=0]
 * @returns {Array<{segments: Array<{text: string, face: string}>, width: number}>}
 */
export function wrapRuns(ctx, textOrRuns, { size, maxWidth, indent = 0, tracking = 0 }) {
  const words = toWords(normalizeRuns(textOrRuns));
  const spaceWidth = measureTracked(ctx.fonts.regular, ' ', size, tracking);
  const lines = [];
  let current = null;

  for (const word of words) {
    const wordWidth = segmentsWidth(ctx, word.segments, size, tracking);
    if (current === null) {
      current = { segments: [...word.segments], width: wordWidth };
      continue;
    }
    // The line being filled is at index lines.length, so line 0 is the only
    // one that gets the undiminished width.
    const available = lines.length === 0 ? maxWidth : maxWidth - indent;
    if (current.width + spaceWidth + wordWidth > available) {
      lines.push(current);
      current = { segments: [...word.segments], width: wordWidth };
    } else {
      current.segments.push({ text: ' ', face: 'regular' });
      current.segments.push(...word.segments);
      current.width += spaceWidth + wordWidth;
    }
  }
  if (current !== null) lines.push(current);
  return lines.length > 0 ? lines : [{ segments: [], width: 0 }];
}

/** Flatten a wrapped line back to plain text. */
export function lineText(line) {
  return line.segments.map((s) => s.text).join('');
}

/** Draw one wrapped line, advancing the pen across faces. Returns the end x. */
function drawLine(ctx, page, line, x, baseline, size, color, tracking) {
  let penX = x;
  for (const seg of line.segments) {
    // Whitespace advances the pen without a drawText call.
    if (!/^\s+$/.test(seg.text)) {
      drawTracked(page, seg.text, {
        x: penX,
        y: baseline,
        font: ctx.fonts[seg.face],
        size,
        color,
        tracking,
        overflow: ctx.overflow(),
      });
    }
    penX += measureTracked(ctx.fonts[seg.face], seg.text, size, tracking);
  }
  return penX;
}

/**
 * Draw a run array as wrapped prose on the current page. Does NOT paginate —
 * use drawBlocks for that.
 *
 * `y` is the baseline of the first line. The return value is the baseline the
 * next line would occupy.
 *
 * @param {Object} ctx
 * @param {Array|string} runs
 * @param {Object} opts
 * @param {number} opts.x
 * @param {number} opts.y baseline of the first line
 * @param {number} [opts.size]
 * @param {number} [opts.leading]
 * @param {number} [opts.maxWidth]
 * @param {Object} [opts.color]
 * @param {number} [opts.indent=0] hanging indent for continuation lines
 * @param {number} [opts.tracking=0]
 * @returns {number} the new y
 */
export function drawRuns(ctx, runs, {
  x,
  y,
  size = TYPE.bodyParagraph.size,
  leading = BODY_LEADING,
  maxWidth = CONTENT_WIDTH,
  color,
  indent = 0,
  tracking = 0,
} = {}) {
  const page = ctx.pages[ctx.pages.length - 1];
  const ink = color || ctx.colors.text;
  const lines = wrapRuns(ctx, runs, { size, maxWidth, indent, tracking });

  lines.forEach((line, i) => {
    // First line at x; continuation lines hang in by `indent`.
    drawLine(ctx, page, line, i === 0 ? x : x + indent, y - i * leading, size, ink, tracking);
  });

  return y - lines.length * leading;
}

/**
 * Draw a paragraph — a plain string or a run array — at §1.3's bodyParagraph
 * scale (9.5 pt) with §3.4's 13 pt leading.
 *
 * @param {Object} ctx
 * @param {string|Array} textOrRuns
 * @param {Object} [opts] as drawRuns
 * @returns {number} the new y
 */
export function drawParagraph(ctx, textOrRuns, opts = {}) {
  return drawRuns(ctx, textOrRuns, {
    x: MARGIN_LEFT,
    size: TYPE.bodyParagraph.size,
    leading: BODY_LEADING,
    maxWidth: CONTENT_WIDTH,
    ...opts,
  });
}

/**
 * Render a bodyBlocks array from proposalLockedCopy, paginating as it goes.
 *
 * Accepts the four shapes that module exports: a plain string, a bare run array,
 * { heading, text? }, and { bullet: true, runs }. Headings render at TYPE.label;
 * §3.4's 8 pt gap separates blocks.
 *
 * ctx.ensureSpace is called before EVERY line, not once per block, so a long
 * paragraph splits across a page boundary instead of overflowing the footer.
 *
 * hangingIndent applies to blocks tagged `bullet: true` — the flag carries the
 * markdown list marker forward from the source. A bare run array is emphasised
 * prose, not a list item, and gets no indent. A bullet glyph, when one is added,
 * belongs at x on the first line.
 *
 * @param {Object} ctx
 * @param {Array} blocks
 * @param {Object} [opts]
 * @param {number} [opts.x]
 * @param {number} [opts.maxWidth]
 * @param {number} [opts.size]
 * @param {number} [opts.leading]
 * @param {Object} [opts.color]
 * @param {number} [opts.gap] paragraph gap, defaults to §3.4's 8 pt
 * @param {number} [opts.hangingIndent=0]
 * @returns {number} ctx.cursor after the last block
 */
export function drawBlocks(ctx, blocks, {
  x = MARGIN_LEFT,
  maxWidth = CONTENT_WIDTH,
  size = TYPE.bodyParagraph.size,
  leading = BODY_LEADING,
  color,
  gap = PARAGRAPH_GAP,
  hangingIndent = 0,
  tracking = 0,
} = {}) {
  if (ctx.pages.length === 0) ctx.newPage();
  const ink = color || ctx.colors.text;
  const headSpec = TYPE.label;

  /** Draw pre-wrapped lines one at a time, breaking pages between them. */
  const emit = (lines, lineX, indent, lineSize, lineFontColor) => {
    lines.forEach((line, i) => {
      ctx.ensureSpace(leading);
      const page = ctx.pages[ctx.pages.length - 1];
      drawLine(
        ctx,
        page,
        line,
        i === 0 ? lineX : lineX + indent,
        ctx.cursor,
        lineSize,
        lineFontColor,
        tracking,
      );
      ctx.cursor -= leading;
    });
  };

  blocks.forEach((block, bi) => {
    if (bi > 0) ctx.cursor -= gap;

    if (Array.isArray(block)) {
      // Bare run array: emphasised prose, not a list item. No indent.
      emit(wrapRuns(ctx, block, { size, maxWidth, indent: 0, tracking }), x, 0, size, ink);
      return;
    }

    if (block !== null && typeof block === 'object' && block.bullet === true) {
      emit(
        wrapRuns(ctx, block.runs, { size, maxWidth, indent: hangingIndent, tracking }),
        x,
        hangingIndent,
        size,
        ink,
      );
      return;
    }

    if (block !== null && typeof block === 'object') {
      const headingLines = wrapRuns(ctx, block.heading, {
        size: headSpec.size,
        maxWidth,
        indent: 0,
        tracking: headSpec.tracking,
      });
      // Headings are set in the label face regardless of run marks.
      emit(
        headingLines.map((l) => ({
          ...l,
          segments: l.segments.map((s) => ({ ...s, face: headSpec.font })),
        })),
        x,
        0,
        headSpec.size,
        ink,
      );
      if (block.text !== undefined && block.text !== null && block.text !== '') {
        ctx.cursor -= HEADING_GAP;
        emit(wrapRuns(ctx, block.text, { size, maxWidth, indent: 0, tracking }), x, 0, size, ink);
      }
      return;
    }

    // Plain string paragraph.
    emit(wrapRuns(ctx, block, { size, maxWidth, indent: 0, tracking }), x, 0, size, ink);
  });

  return ctx.cursor;
}
